/// <amd-module name="Build/ApprovalsPanel/ApprovalPanel" />
import * as React from "react";

import { ApprovalReassignDialog } from "Build/ApprovalsPanel/ApprovalReassignDialog";
import { IChecksPanelService } from "Build/ApprovalsPanel/ChecksPanelService.types";
import { getResourceUrl } from "Build/ApprovalsPanel/ResourceLinks";
import * as Resources from "Build/ApprovalsPanel/Resources";
import * as Utilities from "Build/ApprovalsPanel/Utilities";
import { ApprovalPermissions, ApprovalStatus } from "Build/Client/Contracts/Approval";
import { ApprovalDetails, ApprovalStep } from "Build/Client/Contracts/DataProvider";

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { useComponentContext } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";
import { DatePicker } from "VSSUI/DatePicker";
import { SplitButton } from "VSSUI/SplitButton";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IMenuItem, MoreButton } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { Status, StatusSize } from "VSSUI/Status";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { ago } from "VSSUI/Utilities/Date";
import { VssPersona } from "VSSUI/VssPersona";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { MaxDeferralDurationInDays } from "./Constants";

export interface IApprovalPanelProps {
    approval: ApprovalDetails;
    stageName: string;
    onBackClick: (event: React.SyntheticEvent<HTMLElement>) => void;
    onSubmitApprovals: (status: ApprovalStatus, approvals: ApprovalDetails[], message?: string, deferredTo?: Date | null) => void;
    onReassignStep: (approval: ApprovalDetails, step: ApprovalStep, reassignTo: IdentityRef, message?: string) => Promise<void>;
    onDismiss: () => void;
}

export const ApprovalPanel: React.SFC<IApprovalPanelProps> = props => {

    const name = props.approval.resource ? props.approval.resource.name : "";
    const approvalStatus = Utilities.checksToApprovalStatus(props.approval.status);
    const statusIndicatorData = Utilities.getApprovalStatusIndicatorData(approvalStatus);
    const resourceIconName = props.approval.resource ? Utilities.getResourceTypeIcons(props.approval.resource.type) : "Server";
    let isApprovalInSequence: boolean = false;
    let isReassignDialogOpen = new ObservableValue<boolean>(false);
    let selectedApprovalStep: ApprovalStep;

    //Returns various cases of approval runs
    const getApprovalDetails = (approval: ApprovalDetails) => {
        if (approval.minRequiredApprovers === 0 || approval.minRequiredApprovers === approval.steps.length) {
            if (approval.steps.length === 1) {
                return Resources.DefaultApproval;
            }
            else {
                if (approval.steps[0].order !== approval.steps[1].order) {
                    isApprovalInSequence = true;
                    return Resources.ApprovalInSequence;
                }
                else {
                    return Resources.DefaultApproval;
                }
            }
        }
        else {
            return approval.minRequiredApprovers === 1 ? Resources.SingleMinApproversApproval : format(Resources.MinApproversApproval, approval.minRequiredApprovers)
        }
    }

    const onSubmit = (status: ApprovalStatus, message: string, deferredTo: Date | null) => {
        let approvals: ApprovalDetails[] = new Array<ApprovalDetails>();
        approvals.push(props.approval);
        props.onSubmitApprovals(status, approvals, message, deferredTo);
    }

    const context = useComponentContext();
    let isBlockedMessageVisible = isCannotApproveMessageVisible(props.approval);

    let description: string = statusIndicatorData.label;
    if (approvalStatus === ApprovalStatus.Pending) {
        const remainingTimeoutForApproval = Utilities.getRemainingMinutesForCheckTimeout(props.approval.timeout, props.approval.createdDate);
        description = format(Resources.ApprovalTimeForTimeoutFormat, description, Utilities.getCheckTimeoutOrReevaluationString(Resources.Timeout, remainingTimeoutForApproval));
    }

    const reassignDialogClose = () => {
        isReassignDialogOpen.value = false;
    };

    const onReassignMenuClick = (step: ApprovalStep) => {
        selectedApprovalStep = step;
        isReassignDialogOpen.value = true;
    }

    return (
        <Panel
            backButtonProps={{ onClick: props.onBackClick }}
            contentClassName="no-h-padding"
            description={description}
            lightDismiss={true}
            modal={false}
            onDismiss={props.onDismiss}
            titleProps={{ text: Resources.Approval }}
        >
            <div className="flex-column flex-grow">
                <div className="flex-column rhythm-vertical-24 v-scroll-auto flex-grow">
                    <PanelSection title={Resources.Resource}>
                        <div className="flex-row flex-center pipelines-checks-section-header-details">
                            <Icon
                                className="pipelines-resource-icon primary-text"
                                iconName={resourceIconName}
                            />
                            <div className="flex-column flex-grow h-scroll-hidden">
                                <span className="body-m text-ellipsis checks-resource-name">
                                    <Link
                                        className="pipelines-resource-link"
                                        href={getResourceUrl(context.pageContext, props.approval.resource, true)}
                                        target="_blank">
                                        {name}
                                    </Link>
                                </span>
                            </div>
                        </div>
                    </PanelSection>
                    {isBlockedMessageVisible &&
                        <PanelSection>
                            <MessageCard className="currentUser-blocked-card" severity={MessageCardSeverity.Warning}>
                                {Resources.CurrentUserCannotApproveOwnRunMessage}
                            </MessageCard>
                        </PanelSection>
                    }
                    <PanelSection title={Resources.Requirement} >
                        <div className="pipelines-checks-section-header-details">
                            {getApprovalDetails(props.approval)}
                        </div>
                        <div className="flex-column rhythm-vertical-16">
                            <ApprovalsSection
                                approvalSteps={props.approval.steps}
                                submitApprovals={onSubmit}
                                isApprovalInSequence={isApprovalInSequence}
                                resourceApprovalStatus={approvalStatus}
                                onReassignMenuClick={onReassignMenuClick}
                            />
                        </div>
                    </PanelSection>
                    {props.approval.instructions && (
                        <PanelSection title={Resources.InstructionsLabel}>
                            <span className="approval-instructions">{props.approval.instructions}</span>
                        </PanelSection>
                    )}
                    <Observer isDialogOpen={isReassignDialogOpen}>
                        {(dialogProps: { isDialogOpen: boolean }) =>
                            <ApprovalReassignDialog
                                approvalDetails={props.approval}
                                approvalStep={selectedApprovalStep}
                                onCloseDialog={reassignDialogClose}
                                showDialog={dialogProps.isDialogOpen}
                                onReassign={props.onReassignStep}
                            />
                        }
                    </Observer>
                </div>
            </div>
        </Panel>
    );
};

export interface IPanelSectionProps {
    title?: string;
}

export const PanelSection: React.SFC<IPanelSectionProps> = props => {
    return (
        <div className="flex-column bolt-default-horizontal-spacing">
            {props.title &&
                <span className="pipelines-checks-section-header body-m font-weight-semibold">{props.title}</span>
            }
            {props.children}
        </div>
    );
};

interface IApprovalsSection {
    approvalSteps: ApprovalStep[];
    isApprovalInSequence: boolean;
    resourceApprovalStatus: ApprovalStatus;
    submitApprovals: (status: ApprovalStatus, message?: string, deferredTo?: Date | null) => void;
    onReassignMenuClick: (step: ApprovalStep) => void;
}

const ApprovalsSection: React.SFC<IApprovalsSection> = props => {

    let myApprovals = new ObservableArray<ApprovalStep>([]);
    let approvedApprovals = new ObservableArray<ApprovalStep>([]);
    let deferredApprovals = new ObservableArray<ApprovalStep>([]);
    let rejectedApprovals = new ObservableArray<ApprovalStep>([]);
    let timedOutApprovals = new ObservableArray<ApprovalStep>([]);
    let cancelledApprovals = new ObservableArray<ApprovalStep>([]);
    let skippedApprovals = new ObservableArray<ApprovalStep>([]);
    let otherApprovals = new ObservableArray<ApprovalStep>([]);

    //Sorting by arranging groups first followed by individuals.
    const defaultSortedApprovalSteps = props.approvalSteps.sort(Utilities.approversSort);

    const categorizeApprovals = (approvalSteps: ApprovalStep[], isApprovalInSequence: boolean): void => {
        !isApprovalInSequence && approvalSteps.map((approval) => {
            if (approval.permissions === ApprovalPermissions.Update && approval.status === ApprovalStatus.Pending) {
                myApprovals.value.push(approval);
            }
            else if (approval.status === ApprovalStatus.Deferred)
            {
                deferredApprovals.value.push(approval);
            }
            else if (approval.status === ApprovalStatus.Approved) {
                approvedApprovals.value.push(approval);
            }
            else if (approval.status === ApprovalStatus.Rejected) {
                rejectedApprovals.value.push(approval);
            }
            else if (approval.status === ApprovalStatus.TimedOut) {
                timedOutApprovals.value.push(approval);
            }
            else if (approval.status === ApprovalStatus.Canceled) {
                cancelledApprovals.value.push(approval);
            }
            else if (approval.status === ApprovalStatus.Skipped) {
                skippedApprovals.value.push(approval);
            }
            else if (approval.permissions === ApprovalPermissions.View ||
                approval.permissions === ApprovalPermissions.Reassign) {
                otherApprovals.value.push(approval);
            }
        });
    }

    categorizeApprovals(defaultSortedApprovalSteps, props.isApprovalInSequence);

    //Function to show only approved and rejected approvals, when the approval on the resource is rejected.(Sequence case)
    const getApproversInSequence = (approvers: ApprovalStep[]): ApprovalStep[] => {
        let approverList: ApprovalStep[] = approvers;
        if (props.resourceApprovalStatus === ApprovalStatus.Rejected || props.resourceApprovalStatus === ApprovalStatus.Skipped) {
            approverList = approvers.filter(
                (a) => a.status === ApprovalStatus.Approved || a.status === ApprovalStatus.Rejected || a.status === ApprovalStatus.Skipped
            );
        }
        return approverList;
    }
    //Sorting based on StepId to display approvers in sequence.
    const sortedApprovers = props.isApprovalInSequence ? props.approvalSteps.sort(Utilities.stepSort) : [];
    const sortedApproversInSequence = getApproversInSequence(sortedApprovers);

    //Custom sorting done to bump up individual's priority over groups
    const sortedMyApprovals = myApprovals.value.sort(Utilities.myApprovalsSort);

    const onSubmitApprovals = (status: ApprovalStatus, commentMessage: string, deferredTo: Date | null) => {
        props.submitApprovals(status, commentMessage, deferredTo);
    }

    return (
        <div>
            {!props.isApprovalInSequence &&
                <div>
                    {sortedMyApprovals.length > 0 && props.resourceApprovalStatus === ApprovalStatus.Pending &&
                        <CategorizedApprovals
                            approvalSteps={sortedMyApprovals}
                            isApprovalInSequence={props.isApprovalInSequence}
                            onReassignMenuClick={props.onReassignMenuClick}
                        >
                            <div className="flex-row secondary-text checks-group-header-row">
                                {Resources.WaitingOnMe}
                            </div>
                            <ApprovalOptionsGroup isApprovalInSequence={props.isApprovalInSequence} myApprovalsCount={sortedMyApprovals.length} onSubmit={onSubmitApprovals} />
                        </CategorizedApprovals>
                    }
                    {otherApprovals.value.length > 0 && props.resourceApprovalStatus === ApprovalStatus.Pending &&
                        <CategorizedApprovals
                            approvalSteps={otherApprovals.value}
                            isApprovalInSequence={props.isApprovalInSequence}
                            onReassignMenuClick={props.onReassignMenuClick}
                        >
                            <div className="flex-row secondary-text checks-group-header-row">
                                {Resources.Others}
                            </div>
                        </CategorizedApprovals>
                    }
                    {rejectedApprovals.length > 0 &&
                        <CategorizedApprovals
                            approvalSteps={rejectedApprovals.value}
                            isApprovalInSequence={props.isApprovalInSequence}
                            onReassignMenuClick={props.onReassignMenuClick}
                        />
                    }
                    {approvedApprovals.length > 0 &&
                        <CategorizedApprovals
                            approvalSteps={approvedApprovals.value}
                            isApprovalInSequence={props.isApprovalInSequence}
                            onReassignMenuClick={props.onReassignMenuClick}
                        />
                    }
                    {deferredApprovals.length > 0 &&
                        <CategorizedApprovals
                            approvalSteps={deferredApprovals.value}
                            isApprovalInSequence={props.isApprovalInSequence}
                            onReassignMenuClick={props.onReassignMenuClick}
                        />
                    }
                    {timedOutApprovals.length > 0 &&
                        <CategorizedApprovals
                            approvalSteps={timedOutApprovals.value}
                            isApprovalInSequence={props.isApprovalInSequence}
                            onReassignMenuClick={props.onReassignMenuClick}
                        />
                    }
                    {cancelledApprovals.length > 0 &&
                        <CategorizedApprovals
                            approvalSteps={cancelledApprovals.value}
                            isApprovalInSequence={props.isApprovalInSequence}
                            onReassignMenuClick={props.onReassignMenuClick}
                        />
                    }
                    {skippedApprovals.length > 0 && (
                        <CategorizedApprovals
                            approvalSteps={skippedApprovals.value}
                            isApprovalInSequence={props.isApprovalInSequence}
                            onReassignMenuClick={props.onReassignMenuClick}
                        />
                    )}
                </div>
            }
            {props.isApprovalInSequence &&
                <div>
                    {sortedApproversInSequence.length > 0 &&
                        <div>
                            {sortedApproversInSequence.map((approval: ApprovalStep) => {
                                if (approval.permissions === ApprovalPermissions.Update && approval.status === ApprovalStatus.Pending && props.resourceApprovalStatus === ApprovalStatus.Pending) {
                                    return <ApprovalOptionsGroup
                                        isApprovalInSequence={props.isApprovalInSequence}
                                        approver={approval.assignedApprover}
                                        approvalStatus={approval.status}
                                        myApprovalsCount={1}
                                        onSubmit={onSubmitApprovals}
                                        onReassignMenuClick={props.onReassignMenuClick}
                                    />
                                }
                                else {
                                    return <ApprovalStepRow
                                        approvalStep={approval}
                                        isApprovalInSequence={props.isApprovalInSequence}
                                        onReassignMenuClick={props.onReassignMenuClick}
                                    />
                                }
                            })}
                        </div>
                    }
                </div>
            }
        </div>
    );
}

export interface ICategorizedApprovalsProps {
    approvalSteps: ApprovalStep[];
    isApprovalInSequence: boolean;
    onReassignMenuClick: (step: ApprovalStep) => void;
}

export const CategorizedApprovals: React.SFC<ICategorizedApprovalsProps> = props => {
    return (
        <div>
            {props.children}
            {props.approvalSteps.map(
                (approval: ApprovalStep) => {
                    return <ApprovalStepRow
                        approvalStep={approval}
                        isApprovalInSequence={props.isApprovalInSequence}
                        onReassignMenuClick={props.onReassignMenuClick}
                    />
                })
            }
        </div>
    );
}

export interface IApprovalOptionsGroupProps {
    myApprovalsCount: number;
    isApprovalInSequence: boolean;
    useUpdatedChecksFormat?: boolean;
    approvalStatus?: ApprovalStatus;
    approver?: IdentityRef;
    onSubmit: (status: ApprovalStatus, message: string, deferredTo?: Date | null) => void;
    onReassignMenuClick?: (step: ApprovalStep) => void;
}

export const ApprovalOptionsGroup: React.SFC<IApprovalOptionsGroupProps> = props => {
    const context = useComponentContext();
    const [approvalComment, setComment] = React.useState<string>("");
    const displayName = props.approver ? props.approver.displayName : undefined;
    const [showSpinner, setShowSpinner] = React.useState<boolean>(false);
    const [showDeferral, setShowDeferral] = React.useState<boolean>(false);
    let imageUrl = props.approver ? props.approver.imageUrl : undefined;
    if (!imageUrl && props.approver) {
        imageUrl = getIdentityImageUrl(context.pageContext, props.approver);
    }

    const deferredApprovalsEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Approvals.EnableDeferredApprovals", false)

    const onApproveClick = () => {
        setShowSpinner(true);
        props.onSubmit(ApprovalStatus.Approved, approvalComment);
    }

    const onRejectClick = () => {
        setShowSpinner(true);
        props.onSubmit(ApprovalStatus.Rejected, approvalComment);
    }

    const onCommentChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, val: string) => {
        setComment(val);
    };

    const onDefer = (date: Date) => {
        setShowSpinner(true);
        props.onSubmit(ApprovalStatus.Deferred, approvalComment, date);
    }

    const onReassignClick = () => {
        let step = {
            assignedApprover: props.approver
        } as ApprovalStep

        props.onReassignMenuClick && props.onReassignMenuClick(step);
    }

    const reassignMenuItems: IMenuItem[] = [
        { id: "reassign", text: Resources.Reassign, onActivate: onReassignClick }
    ];

    let isMoreButtonVisible = props.isApprovalInSequence;

    const deferMenuItems: IMenuItem[] = [
        { id: "defer", text: Resources.DeferApproval, onActivate: () => { setShowDeferral(true);} }
    ];

    return (
        <div className={css("flex-column", "approval-options-group")}>
            <div className={css("flex-row", props.useUpdatedChecksFormat ? "approval-options" : (props.isApprovalInSequence ? "approval-options-sequence-case" : "approval-options-default"))}>
                {props.isApprovalInSequence && props.approvalStatus &&
                    <div className="flex-row flex-center">
                        <Status {...Utilities.getApprovalStatusIndicatorData(props.approvalStatus).statusProps} className="pipelines-user-approval-state" size={StatusSize.m} />
                        <VssPersona suppressPersonaCard={true} isTabStop={false} className="pipelines-user-approval-persona" displayName={displayName} imageUrl={imageUrl} size="small" />
                    </div>
                }
                <div className="flex-row flex-center">
                    <TextField
                        className={deferredApprovalsEnabled ?
                            props.useUpdatedChecksFormat ?
                            (props.myApprovalsCount > 1 ? "approval-comment-box-multiple-defer" : "approval-comment-box-defer") :
                            (props.myApprovalsCount > 1 ? "approval-comment-box-width-default-defer" : (props.isApprovalInSequence ? "approval-comment-box-width-sequence-defer" : "approval-comment-box-width-many-defer")) :
                            props.useUpdatedChecksFormat ?
                            (props.myApprovalsCount > 1 ? "approval-comment-box-multiple" : "approval-comment-box") :
                            (props.myApprovalsCount > 1 ? "approval-comment-box-width-default" : (props.isApprovalInSequence ? "approval-comment-box-width-sequence" : "approval-comment-box-width-many"))}
                        rows={1}
                        disabled={showSpinner}
                        multiline={true}
                        onChange={onCommentChanged}
                        value={approvalComment}
                        placeholder={Resources.Comment} />
                    {showSpinner ? <Spinner className="pipeline-approval-spinner" /> : <div />}
                </div>
                <div className="approval-reject-button">
                    <Button
                        disabled={showSpinner}
                        text={props.myApprovalsCount > 1 ? Resources.RejectAll : Resources.Reject}
                        onClick={onRejectClick} />
                </div>
                {!deferredApprovalsEnabled &&
                    <div className="approval-approve-button">
                    <Button
                        disabled={showSpinner}
                        text={props.myApprovalsCount > 1 ? Resources.ApproveAll : Resources.Approve}
                        onClick={onApproveClick}
                        primary={true} />
                    </div>
                }
                {deferredApprovalsEnabled && !showDeferral &&
                    <div className="approval-approve-splitbutton">
                        <SplitButton
                            disabled={showSpinner}
                            buttonProps={{
                                text: props.myApprovalsCount > 1 ? Resources.ApproveAll : Resources.Approve,
                                onClick: onApproveClick
                            }}
                            menuButtonProps={{
                                contextualMenuProps: {
                                    menuProps: {
                                        id: "1",
                                        items: deferMenuItems
                                    }
                                }
                            }}
                            primary={true} />
                    </div>
                }
                {isMoreButtonVisible &&
                    <MoreButton
                        className="approval-more-button"
                        role="menuitem"
                        contextualMenuProps={{
                            menuProps: { id: "header-submenu", items: reassignMenuItems }
                        }}
                    />
                }
            </div>
            {showDeferral &&
                <div className={css("flex-row", props.useUpdatedChecksFormat ? "approval-options" : "approval-defer-options")}>
                    <DeferApprovalArea
                        onDefer={onDefer}
                        onCancel={() => setShowDeferral(false)}
                    />
                </div>
            }
        </div>
    );
}

export interface IDeferApprovalAreaProps {
    deferredTo?: Date | null;
    onDefer: (deferredTo: Date) => void;
    onCancel: () => void;
}

export const DeferApprovalArea: React.SFC<IDeferApprovalAreaProps> = props => {
    const [deferredTo, setDeferredTo] = React.useState<ObservableValue<Date>>(new ObservableValue<Date>(props.deferredTo ?? new Date()));

    const hours = new ObservableValue<string>(deferredTo.value.getHours().toString());
    const minutes = new ObservableValue<string>(deferredTo.value.getMinutes().toString());

    const minDeferredDate = new Date();
    let maxDeferredDate = new Date();
    maxDeferredDate.setDate(maxDeferredDate.getDate()+MaxDeferralDurationInDays);

    const onDateChanged = (date: Date) => {
        if (deferredTo != null) {
            date.setHours(deferredTo.value.getHours());
            date.setMinutes(deferredTo.value.getMinutes());

            setDeferredTo(new ObservableValue<Date>(date));
        }
    }

    const onHoursChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, hoursString: string) => {
        const parsedResult = hoursString.match(/\d+/);
        let hoursNum = parsedResult ? parseInt(parsedResult[0]) : 0;

        if (hoursNum >=0 && hoursNum<=23) {
            deferredTo.value.setHours(hoursNum);
            setDeferredTo(deferredTo);
            hours.value = hoursNum.toString();
        }
    }

    const onMinutesChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, minutesString: string) => {
        const parsedResult = minutesString.match(/\d+/);
        let minutesNum = parsedResult ? parseInt(parsedResult[0]) : 0;

        if (minutesNum >=0 && minutesNum <= 59) {
            deferredTo.value.setMinutes(minutesNum);
            setDeferredTo(deferredTo);
            minutes.value = minutesNum.toString();
        }
    }

    const onApproveAndDeferClick = () => {
        props.onDefer(deferredTo.value);
    }

    const getTimeZoneText = ():string => {
        let offsetHours = new Date().getTimezoneOffset()/60;
        let offsetMinutes = (new Date().getTimezoneOffset() % 60).toString();
        if (offsetMinutes == "0") offsetMinutes = "00";
        let sign = offsetHours == 0 ? "" : offsetHours > 0 ? "-" : "+";
        return "(UTC"+sign+Math.abs(offsetHours)+":"+offsetMinutes+") "+ Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    return (
        <div className={css("flex-column")}>
            <div className={css("flex-row", "approval-defer-date-row")}>
                <div className={css("flex-column", "approval-defer-date-column")}>
                    <label>{Resources.Date}</label>
                    <DatePicker
                        className="approval-defer-date"
                        value={deferredTo}
                        onChange={onDateChanged}
                        minDate={minDeferredDate}
                        maxDate={maxDeferredDate}
                    />
                </div>
                <div className={css("flex-column", "approval-defer-date-column")}>
                    <label>{Resources.Hours}</label>
                    <TextField
                        className="approval-defer-hours"
                        onChange={onHoursChanged}
                        value={hours}
                        inputType="number"
                    />
                </div>
                <div className={css("flex-column", "approval-defer-date-column")}>
                    <label>{Resources.Minutes}</label>
                    <TextField
                        className="approval-defer-minutes"
                        onChange={onMinutesChanged}
                        value={minutes}
                        inputType="number"
                    />
            </div>
        </div>
        <div className={css("flex-column", "time-zone-title")}>{Resources.TimeZone}: {getTimeZoneText()}</div>
        <div className="flex-row">
            <div className="approval-defer-button">
            <Button
                text={props.deferredTo ? Resources.Save : Resources.ApproveAndDefer}
                onClick={onApproveAndDeferClick} 
                primary={true}/>
            </div>
            <div className="approval-cancel-deferral-button">
            <Button
                text={Resources.Cancel}
                onClick={() => {
                    props.onCancel();
                }} />
            </div>
        </div>
    </div>
    );
}

interface IApprovalRowProps {
    approvalStep: ApprovalStep;
    isApprovalInSequence: boolean;
    onReassignMenuClick: (step: ApprovalStep) => void;
}

const ApprovalStepRow: React.SFC<IApprovalRowProps> = props => {
    const context = useComponentContext();
    const step = props.approvalStep;
    const statusIndicatorData = Utilities.getApprovalStatusIndicatorData(step.status);
    const approvalStatusIndicator = Utilities.getApprovalStatus(step.status)
    const approver = ((step.status === ApprovalStatus.Pending) || (step.status === ApprovalStatus.Uninitiated) || (step.status === ApprovalStatus.Canceled) || (step.status === ApprovalStatus.TimedOut)) ? step.assignedApprover : step.actualApprover;
    let displayName = approver && approver.displayName;
    if (displayName && step.assignedApprover && step.assignedApprover.id != approver.id) {
        displayName += format(Resources.OnBehalfOfFormat, step.assignedApprover.displayName);
    }
    if (displayName && step.isStepReassigned) {
        displayName = format(Resources.Reassigned, displayName);
    }
    let imageUrl = approver && approver.imageUrl;
    if (!imageUrl && approver) {
        imageUrl = getIdentityImageUrl(context.pageContext, approver);
    }

    const onReassignClick = () => {
        props.onReassignMenuClick(props.approvalStep);
    }

    const menuItems: IMenuItem[] = [
        { id: "reassign", text: Resources.Reassign, onActivate: onReassignClick }
    ];

    let isMoreButtonVisible = (step.permissions === ApprovalPermissions.Update && step.status != ApprovalStatus.Deferred || step.permissions === ApprovalPermissions.Reassign );

    return (
        <div className="flex-column">
            <div className="pipelines-user-approval-row flex-row flex-center">
                <Status {...statusIndicatorData.statusProps} className="pipelines-user-approval-state" size={StatusSize.m} />
                {approver && (
                    <VssPersona suppressPersonaCard={true} isTabStop={false} className="pipelines-user-approval-persona" displayName={approver.displayName} imageUrl={imageUrl} size="small" />
                )}
                <div className={css("flex-column flex-grow", "pipelines-user-approval-text")}>
                    <span className="body-m text-ellipsis">{displayName}</span>
                    <span className="body-s text-ellipsis secondary-text">{step.status === ApprovalStatus.Pending ? approvalStatusIndicator : format(approvalStatusIndicator, ago(step.lastModifiedOn))}</span>
                </div>
                {isMoreButtonVisible &&
                    <MoreButton
                        contextualMenuProps={{
                            menuProps: { id: "header-submenu", items: menuItems }
                        }}
                    />
                }
            </div>
            {props.approvalStep.comment && (
                <div className="pipelines-approval-comment-row flex-row">
                    <span className="body-m">{props.approvalStep.comment}</span>
                </div>
            )}
        </div>
    );
};

function isCannotApproveMessageVisible(approval: ApprovalDetails): boolean {
    const context = useComponentContext();
    const service = context.pageContext.getService<IChecksPanelService>("IChecksPanelService");
    let approvals: ApprovalDetails[] = new Array<ApprovalDetails>();
    approvals.push(approval);

    return service.isCurrentUserBlockedFromApproving(approvals);
}